import './index.scss';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Form } from 'semantic-ui/semantic-ui-imports';
import { groupAddons } from 'services/addons';
import StubPlacementInputs from 'components/StubPlacementInputs';
import AddonForm from '../../components/AddonForm';
import { useData } from '../../contexts/data';
import { AddonOption, AddonCategory } from '../../Types/Aggregates';

function ChooseAddonsPage() {
  const { t } = useTranslation();
  const {
    setActiveUserInputs,
    activeUserInputs,
    activeAggregate,
    fetchAvailableAddons,
    setAvailableAddons,
    availableAddons,
  } = useData();
  const [addons, setAddons] = React.useState<Array<AddonOption>>([]);
  const [inputs, setInputs] = React.useState(activeUserInputs);
  const navigate = useNavigate();
  const [powerSupply, setPowerSupply] = React.useState<string>(
    activeUserInputs.power_supply
  );

  const [addonCategories, setAddonCategories] = React.useState<
    Array<AddonCategory>
  >([]);

  // Track if we've restored saved addons to avoid overwriting user changes
  const hasRestoredSavedAddonsRef = React.useRef(false);

  // Load available addons when aggregate/power supply is set
  const loadAddons = async () => {
    if (!activeAggregate?.id || !powerSupply) return;
    const data = await fetchAvailableAddons(activeAggregate.id, powerSupply);
    if (data?.length) {
      setAvailableAddons(data);
    }
  };

  React.useEffect(() => {
    // Reset restoration flag when power supply changes (new addon set)
    hasRestoredSavedAddonsRef.current = false;
    loadAddons();
  }, [powerSupply, activeAggregate?.id]);

  // Separate effect: resolve saved addon IDs once availableAddons are loaded
  React.useEffect(() => {
    if (!availableAddons?.length) return;

    // Build categories for display
    const categories = groupAddons(availableAddons, true, true);
    setAddonCategories(categories);

    // Only restore saved addons once per session (avoid overwriting user changes)
    if (hasRestoredSavedAddonsRef.current) return;
    hasRestoredSavedAddonsRef.current = true;

    // Check if we have saved addon IDs to restore from activeUserInputs
    const savedAddons = activeUserInputs?.addons;
    if (savedAddons?.length) {
      const firstSaved = savedAddons[0];
      if (typeof firstSaved === 'number' || typeof firstSaved === 'string') {
        // savedAddons are IDs, resolve them
        const resolved = savedAddons
          .map((id: number | string) =>
            availableAddons.find((a) => a.id === Number(id))
          )
          .filter(Boolean);
        if (resolved.length) {
          setAddons(resolved);
          return;
        }
      } else if (firstSaved?.id) {
        // savedAddons are full objects, use them directly
        setAddons(savedAddons);
        return;
      }
    }

    // Default: select only mandatory addons
    const mandatoryAddons = availableAddons.filter((a) => a.mandatory);
    setAddons(mandatoryAddons);
  }, [availableAddons, activeUserInputs?.addons]);

  // Sync local inputs when activeUserInputs changes (e.g., result loaded from URL)
  // Use a ref to only sync on initial mount or when key fields differ
  const initialSyncRef = React.useRef(true);
  React.useEffect(() => {
    if (
      initialSyncRef.current &&
      activeUserInputs &&
      Object.keys(activeUserInputs).length > 0
    ) {
      setInputs(activeUserInputs);
      if (activeUserInputs.power_supply) {
        setPowerSupply(activeUserInputs.power_supply);
      }
      initialSyncRef.current = false;
    }
  }, [activeUserInputs]);

  React.useEffect(() => {
    if (inputs?.power_supply !== powerSupply) {
      setPowerSupply(inputs.power_supply);
    }
  }, [inputs]);

  const onSubmit = () => {
    setActiveUserInputs({ ...inputs, addons });
    navigate('/results');
  };

  const onAddonSelect = (addon: AddonOption) => {
    // Prevent duplicates
    if (addons.some((a) => a.id === addon.id)) return;
    const newAddons = [...addons, addon];
    setAddons(newAddons);
    setActiveUserInputs({ ...activeUserInputs, addons: newAddons });
  };

  const onAddonDeSelect = (addon: AddonOption) => {
    const newAddons = addons.filter((a) => a.id !== addon.id);
    setAddons(newAddons);
    setActiveUserInputs({ ...activeUserInputs, addons: newAddons });
  };

  return (
    <div className="choose-addon-page">
      <h2 className="choose-addon-page__header">
        {t(`chooseAddonPage.header`)} - {activeAggregate?.name}
      </h2>
      <Form onSubmit={onSubmit}>
        <div className="choose-addon-page__content">
          <div className="choose-addon-page__content__agrregate-info">
            <div className="choose-addon-page__content__stub-placement">
              <div className="choose-addon-page__content__stub-placement__title">
                {t('productPage.placement.title')}
              </div>
              <StubPlacementInputs inputs={inputs} setInputs={setInputs} />
            </div>
          </div>
          {/* {!isExpertPage && (
          )} */}
          <div className="choose-addon-page__content__addon-form">
            <AddonForm
              categories={addonCategories}
              selectedAddons={addons}
              onSelect={onAddonSelect}
              onDeselect={onAddonDeSelect}
            />
          </div>
        </div>
        <div className="choose-addon-page__btns footer">
          <Button
            onClick={() => {
              navigate(`/configure/${activeAggregate?.id}`);
            }}
          >
            {'<'} {t('navigation.back')}
          </Button>
          <Button type="submit" primary>
            {t(`chooseAddonPage.buttonText`)}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default ChooseAddonsPage;
